import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import CEUFormView from '../components/forms/CEUFormView';
import ceuService from '../services/unifiedCEUService';
import { getRequirements, getSubcategoryOptions } from '../config/ceuConfig';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const ceuSchema = yup.object({
  workshopName: yup.string().required('Workshop title is required'),
  instructor: yup.string().required('Instructor name is required'),
  provider: yup.string(),
  organization: yup.string(),
  date: yup.string().required('Date is required'),
  ceuHours: yup.number()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .required('CEUs are required')
    .positive('Hours must be positive')
    .max(100, 'Hours cannot exceed 100'),
  description: yup.string(),
  certificateFile: yup
    .mixed()
    .nullable()
    .test('fileSize', 'File size must be less than 10MB', (value) => {
      if (!value) return true; // Optional field
      return value.size <= 10 * 1024 * 1024; // 10MB
    })
    .test('fileType', 'Only PDF, PNG, JPG, and JPEG files are allowed', (value) => {
      if (!value) return true; // Optional field
      return ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'].includes(value.type);
    }),
  category: yup.string().required('Category is required'),
  subCategory: yup.string(),
  processingStatus: yup.object({
    rid: yup.boolean(),
    bei: yup.boolean(),
  }),
});

export default function EditCEU() {
  const { ceuId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const userProfile = {
    certifyingBody: 'RID',
    hasBothCertifications: false,
  };

  const requirements = getRequirements(userProfile.certifyingBody);
  const availableCategories = requirements?.requirements 
    ? Object.keys(requirements.requirements).map(cat => ({
        value: cat,
        label: cat,
        description: '',
      }))
    : [];

  const { control, handleSubmit, watch, reset, formState: { errors } } = useForm({
    resolver: yupResolver(ceuSchema),
    defaultValues: {
      workshopName: '',
      instructor: '',
      provider: '',
      organization: '',
      date: '',
      ceuHours: '',
      description: '',
      category: '',
      subCategory: '',
      processingStatus: {
        rid: false,
        bei: false,
      },
    },
  });

  const selectedCategory = watch('category');
  const availableSubcategories = selectedCategory 
    ? getSubcategoryOptions(userProfile.certifyingBody, selectedCategory)
    : [];

  useEffect(() => {
    loadCEU();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ceuId]);

  const loadCEU = async () => {
    try {
      setLoading(true);
      const result = await ceuService.getCEU(user.userId, ceuId);
      
      if (result.success && result.data) {
        const ceuData = result.data;
        
        reset({
          workshopName: ceuData.title || '',
          instructor: ceuData.instructor || '',
          provider: ceuData.provider || '',
          organization: ceuData.organization || '',
          date: ceuData.date || '',
          ceuHours: ceuData.ceuHours || ceuData.ceuHours || '',
          description: ceuData.description || '',
          category: ceuData.category || '',
          subCategory: ceuData.subCategory?.[0] || ceuData.subcategories?.[0] || '',
          processingStatus: {
            rid: ceuData.ridReceived || false,
            bei: ceuData.beiReceived || false,
          },
        });
      } else {
        console.error('CEU not found or error:', result);
        setError('CEU not found');
      }
    } catch (err) {
      console.error('Failed to load CEU:', err);
      setError('Failed to load CEU');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updates = {
        title: data.workshopName,
        instructor: data.instructor,
        provider: data.provider,
        organization: data.organization,
        date: data.date,
        ceuHours: parseFloat(data.ceuHours),
        description: data.description,
        category: data.category,
        subcategories: data.subCategory ? [data.subCategory] : [],
        ridReceived: data.processingStatus.rid,
        beiReceived: data.processingStatus.bei,
      };

      const result = await ceuService.updateCEU(user.userId, ceuId, updates);
      
      // Service returns the CEU object directly, not wrapped in { success, data }
      if (result && !result.error) {
        setSuccess('CEU updated successfully!');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        throw new Error(result?.error || 'Failed to update CEU');
      }
    } catch (err) {
      console.error('Update error:', err);
      setError(err.message || 'Failed to update CEU');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <CEUFormView
      control={control}
      errors={errors}
      handleSubmit={handleSubmit(onSubmit)}
      isEditing={true}
      loading={saving}
      error={error}
      success={success}
      userProfile={userProfile}
      availableCategories={availableCategories}
      availableSubcategories={availableSubcategories}
      selectedCategory={selectedCategory}
      onBack={() => navigate('/dashboard')}
    />
  );
}
